import { useEffect, useState, useCallback, type ReactNode } from 'react';
import { getCurrentTenantSlug } from '@/lib/tenant';
import { apiClient, ApiClientError } from '@/api';
import {
  TenantContext,
  type TenantState,
  type TenantError,
} from './tenantTypes';
import type { Tenant } from '@/types';

interface TenantProviderProps {
  children: ReactNode;
}

export function TenantProvider({ children }: TenantProviderProps) {
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [slug, setSlug] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<TenantError | null>(null);

  const fetchTenant = useCallback(async (tenantSlug: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const tenantData = await apiClient.get<Tenant>(
        `/api/v1/tenants/${tenantSlug}`
      );
      setTenant(tenantData);
    } catch (err) {
      if (err instanceof ApiClientError) {
        if (err.status === 404) {
          setError({
            type: 'not_found',
            message: `Workspace "${tenantSlug}" not found`,
          });
        } else {
          setError({
            type: 'unknown',
            message: err.message,
          });
        }
      } else if (err instanceof TypeError && err.message.includes('fetch')) {
        setError({
          type: 'network',
          message: 'Unable to connect. Please check your internet connection.',
        });
      } else {
        setError({
          type: 'unknown',
          message: 'An unexpected error occurred',
        });
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  const retry = useCallback(() => {
    if (slug) {
      fetchTenant(slug);
    }
  }, [slug, fetchTenant]);

  useEffect(() => {
    const { slug: extractedSlug, isValid } = getCurrentTenantSlug();

    if (!isValid) {
      setSlug('');
      setIsLoading(false);
      setError({
        type: 'invalid_subdomain',
        message: 'Please access this portal through your workspace subdomain',
      });
      return;
    }

    setSlug(extractedSlug);
    fetchTenant(extractedSlug);
  }, [fetchTenant]);

  const value: TenantState = {
    tenant,
    slug,
    isLoading,
    error,
    retry,
  };

  return <TenantContext value={value}>{children}</TenantContext>;
}
